import React, { useEffect, useState, useCallback, useRef } from "react";
import { DatePicker } from "./ui/date-picker";
import fetchWithAuth from "../fetchWithAuth";

const API_URL = "/api/project";

function stripProtocolAndWww(url) {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}

function isPromoted(url, promotedPages) {
  return promotedPages.some(p => stripProtocolAndWww(p.url) === stripProtocolAndWww(url));
}

function isBlacklisted(url, blacklistPages) {
  return blacklistPages.some(p => stripProtocolAndWww(p.blackUrl) === stripProtocolAndWww(url));
}

export default function AuditTab({ projectId }) {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [auditData, setAuditData] = useState([]);
  const [queries, setQueries] = useState([]);
  const [promotedPages, setPromotedPages] = useState([]);
  const [blacklistPages, setBlacklistPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const tableWrapperRef = useRef(null);
  const scrollState = useRef({ top: 0, left: 0 });
  const shouldRestoreScroll = useRef(false);
  const pageScrollY = useRef(0);
  const shouldRestorePageScroll = useRef(false);

  // Получить все необходимые данные
  const fetchAll = useCallback(async (date) => {
    setLoading(true);
    try {
      const datesRes = await fetchWithAuth(`${API_URL}/${projectId}/arsenkin-dates`);
      const datesArr = await datesRes.json();
      setDates(datesArr);
      let useDate = date;
      if (!useDate && datesArr.length > 0) {
        useDate = datesArr[0].slice(0, 10);
        setSelectedDate(useDate);
      }
      let audit = [];
      if (useDate) {
        const auditRes = await fetchWithAuth(`${API_URL}/${projectId}/arsenkin?date=${useDate}`);
        audit = await auditRes.json();
      }
      setAuditData(audit);
      const projRes = await fetchWithAuth(`${API_URL}/${projectId}`);
      const proj = await projRes.json();
      setQueries(proj.queries || []);
      setPromotedPages(proj.promotedPages || []);
      setBlacklistPages(proj.blacklistPages || []);
    } catch (e) {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchAll(selectedDate);
    // eslint-disable-next-line
  }, [projectId]);

  // При смене даты
  const handleDateChange = (date, restoreScroll = false, restorePageScroll = false) => {
    if (tableWrapperRef.current) {
      scrollState.current.top = tableWrapperRef.current.scrollTop;
      scrollState.current.left = tableWrapperRef.current.scrollLeft;
      shouldRestoreScroll.current = restoreScroll;
    }
    if (restorePageScroll) {
      pageScrollY.current = window.scrollY;
      shouldRestorePageScroll.current = true;
    }
    setSelectedDate(date);
    fetchAll(date);
  };

  // Восстановление скролла после обновления данных
  useEffect(() => {
    if (!loading) {
      if (shouldRestoreScroll.current && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = scrollState.current.top;
        tableWrapperRef.current.scrollLeft = scrollState.current.left;
        shouldRestoreScroll.current = false;
      }
      if (shouldRestorePageScroll.current) {
        window.scrollTo(0, pageScrollY.current);
        shouldRestorePageScroll.current = false;
      }
    }
  }, [loading]);

  // Обработка стрелок влево/вправо
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!dates || dates.length === 0) return;
      if (!selectedDate) return;
      if (!tableWrapperRef.current) return;
      const idx = dates.findIndex(d => d.slice(0, 10) === selectedDate);
      if (e.key === "ArrowLeft" && idx < dates.length - 1) {
        const prev = dates[idx + 1].slice(0, 10);
        handleDateChange(prev, true, true);
      }
      if (e.key === "ArrowRight" && idx > 0) {
        const next = dates[idx - 1].slice(0, 10);
        handleDateChange(next, true, true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line
  }, [dates, selectedDate]);

  // Формируем структуру: { [query]: [ { position, url } ... ] }
  const queryMap = {};
  for (const q of queries) {
    queryMap[q.queryText] = [];
  }
  for (const row of auditData) {
    if (queryMap[row.key]) {
      queryMap[row.key].push({ position: row.position, url: row.url });
    }
  }
  for (const q in queryMap) {
    queryMap[q].sort((a, b) => a.position - b.position);
  }

  return (
    <div className="w-full">
      <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        Ниже представлен аудит выдачи для региона проекта. Если регионов проекта несколько, то выбирается самый часто повторяющийся в стороке региона, если таких нет - берем первый по списку.
      </div>
      <div className="mb-2">
        <div className="font-bold text-s text-gray-800 mb-1">
          Дата{dates.length > 0 ? ` (доступно ${dates.length})` : ""}:
        </div>
        <div style={{ width: 130 }}>
          <DatePicker
            value={selectedDate ? new Date(selectedDate + 'T00:00:00') : null}
            onChange={d => {
              if (d instanceof Date && !isNaN(d)) {
                // Получаем локальную дату в формате YYYY-MM-DD
                const pad = n => n.toString().padStart(2, '0');
                const localDate =
                  d.getFullYear() +
                  '-' +
                  pad(d.getMonth() + 1) +
                  '-' +
                  pad(d.getDate());
                handleDateChange(localDate);
              }
            }}
            options={dates
              .map(d => d.slice(0, 10))
              .filter(d => d <= new Date().toISOString().slice(0, 10))
            }
            placeholder="Выберите дату"
            markedDates={dates
              .map(d => d.slice(0, 10))
              .filter(d => d <= new Date().toISOString().slice(0, 10))
            }
          />
        </div>
      </div>
      <div
        className="overflow-x-auto overflow-y-auto bg-white rounded-lg border shadow-sm p-0 relative"
        ref={tableWrapperRef}
      >
        {/* Оверлей загрузки */}
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              pointerEvents: "none"
            }}
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
        <table className="min-w-[900px] w-full text-xs font-normal">
          <thead>
            <tr>
<th
                className="px-2 py-0.5 border-b border-gray-200 text-left font-medium text-muted-foreground bg-[#eceeff]"
                style={{ minWidth: 180, maxWidth: 300, height: 36 }}
              >
                Запрос
              </th>
              {Array.from({ length: 20 }).map((_, i) => (
<th
                  key={i}
                  className={
                    "px-2 py-0.5 border-b border-gray-200 text-center font-medium text-muted-foreground bg-[#eceeff]" +
                    (i === 9 ? " border-r-4 border-black" : "")
                  }
                  style={{ minWidth: 90, maxWidth: 120, height: 36 }}
                >
                  {`Топ-${i + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {queries.map(q => (
              <tr key={q.id} style={{ height: 36 }}>
                <td
                  className="px-2 py-0.5 border-b border-gray-100 text-left font-normal text-gray-800"
                  style={{ minWidth: 180, maxWidth: 300, height: 36, verticalAlign: "middle" }}
                >
                  {q.queryText}
                </td>
                {Array.from({ length: 20 }).map((_, i) => {
                  const item = queryMap[q.queryText][i];
                  let cellClass = "px-2 py-0.5 border-b border-gray-100 text-center font-normal";
                  if (i === 9) cellClass += " border-r-4 border-black";
                  let bg = "";
                  if (item && isPromoted(item.url, promotedPages)) bg = "bg-green-100";
                  if (item && isBlacklisted(item.url, blacklistPages)) bg = "bg-red-100";
                  return (
                    <td
                      key={i}
                      className={cellClass + " " + bg}
                      style={{
                        minWidth: 90,
                        maxWidth: 110,
                        height: 36,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle"
                      }}
                    >
                      {item ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-blue-700 block"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            maxWidth: "100%"
                          }}
                          title={stripProtocolAndWww(item.url)}
                        >
                          {stripProtocolAndWww(item.url)}
                        </a>
                      ) : ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 text-xs text-gray-500 px-2 pb-2">
          <span className="inline-block w-4 h-4 align-middle bg-green-100 border border-green-300 mr-1"></span> — страница из продвижения
          <span className="inline-block w-4 h-4 align-middle bg-red-100 border border-red-300 mx-2"></span> — страница на вытеснение
        </div>
      </div>
    </div>
  );
}
